import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Badge, Spinner, Card, Form, Tabs, Tab, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, ENDPOINTS, DEFAULT_STORE_ID } from '../config';
import Navbar from '../components/Navbar';
import './Cart.css';

const Cart: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice, toUSD } = useCurrency();
  const { items, removeFromCart, updateQuantity, clearCart, getTotalPrice, calculateItemPrice, getApplicableTier } = useCart();
  const { user, isAuthenticated, token, logout } = useAuth();
  const storeId = DEFAULT_STORE_ID;

  const getStatusBadge = (status: number, statusName: string) => {
    let variant = 'secondary';
    switch (status) {
      case 1: variant = 'warning'; break; // Pending
      case 2: variant = 'info'; break;    // Processing
      case 3: variant = 'primary'; break; // Shipped
      case 4: variant = 'success'; break; // Delivered
      case 5: variant = 'danger'; break;  // Cancelled
      default: variant = 'secondary';
    }
    return <Badge bg={variant}>{statusName}</Badge>;
  };

  const [cartRequests, setCartRequests] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | null>(null);
  const [shippingMethods, setShippingMethods] = useState<any[]>([]);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('cart');

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchCartRequests();
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
      fetchPaymentMethods();
  }, [i18n.language]);

  useEffect(() => {
      fetchShippingMethods();
  }, [items, i18n.language]);

  useEffect(() => {
    if (shippingMethods.length > 0 && !selectedShippingMethodId) {
      setSelectedShippingMethodId(shippingMethods[0].storeShippingMethodId);
    }
  }, [shippingMethods, selectedShippingMethodId]);

  // Removed automatic deselection of installment methods as they are now available for all orders
  /*
  useEffect(() => {
    const allExternal = items.length > 0 && items.every(i => i.isExternalOrder);
    const isInstallmentMethod = (m: any) => {
      const methodLabel = i18n.language === 'en' && m.nameEn ? m.nameEn : m.nameAr;
      const n = (methodLabel || '').toLowerCase();
      return n.includes('tabby') || n.includes('tamara') || (methodLabel || '').includes('تابي') || (methodLabel || '').includes('تمارا');
    };
    if (!allExternal && selectedPaymentMethodId) {
      const selected = paymentMethods.find(m => m.storePaymentMethodId === selectedPaymentMethodId);
      if (selected && isInstallmentMethod(selected)) {
        setSelectedPaymentMethodId(null);
      }
    }
  }, [paymentMethods, items, selectedPaymentMethodId, i18n.language]);
  */

  const fetchShippingMethods = async () => {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Accept-Language': i18n.language
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      if (items.length === 0) {
          setShippingMethods([]);
          return;
      }

      const externalItems = items.filter(i => i.isExternalOrder);
      if (externalItems.length === 0) {
          setShippingMethods([]);
          return;
      }

      const payload = externalItems.map(i => ({
          storeItemId: i.id,
          quantity: i.quantity
      }));

      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.SHIPPING_PREVIEW}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        // Map response to match existing structure + new fields
        const methods = data.map((m: any) => ({
            storeShippingMethodId: m.shippingMethodId || m.storeShippingMethodId || m.id,
            nameAr: m.nameAr,
            nameEn: m.nameEn,
            cost: m.cost,
            estimatedDays: m.estimatedDays,
            feePercentage: 0 // Backend DTO doesn't have this yet, assuming 0 or add to DTO if needed
        }));
        setShippingMethods(methods || []);
      } else if (response.status === 401) {
        console.warn('Unauthorized access to shipping methods');
      }
    } catch (error) {
      console.error('Error fetching shipping methods:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Accept-Language': i18n.language
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.STORE_PAYMENT_METHODS}?region=KSA`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        const mappedData = data.map((m: any) => ({
            storePaymentMethodId: m.paymentMethodId || m.storePaymentMethodId || m.id,
            nameEn: m.nameEn,
            nameAr: m.nameAr,
            image: m.imageUrl || m.image,
            code: m.code,
            feePercentage: m.feePercentage
        }));
        const hasCash = mappedData.some((m: any) => (m.code || '').toLowerCase() === 'cash' || (m.nameEn || '').toLowerCase().includes('cash') || (m.nameAr || '').includes('نقد'));
        const cashMethod = {
          storePaymentMethodId: -100,
          nameEn: 'Cash',
          nameAr: 'نقدي',
          image: null,
          code: 'CASH',
          feePercentage: 0
        };
        setPaymentMethods(hasCash ? mappedData : [...mappedData, cashMethod]);
      } else if (response.status === 401) {
        console.warn('Unauthorized access to payment methods');
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const fetchCartRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.GET_CART_REQUESTS}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': i18n.language
        }
      });
      
      if (response.status === 401) {
        // Token expired
        logout();
        navigate('/login');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setCartRequests(data);
      }
    } catch (error) {
      console.error('Error fetching cart requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleUpdateQuantity = (itemId: number, newQuantity: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const minQty = item.minimumOrderQuantity || 1;
    if (newQuantity < minQty) {
      Swal.fire({
        icon: 'warning',
        title: t('quantityError') as string,
        text: t('minOrderQuantityMessage', { min: minQty }) as string,
        confirmButtonText: t('ok') as string
      });
      return;
    }
    updateQuantity(itemId, newQuantity);
  };

  const getShippingCost = () => {
    if (!selectedShippingMethodId) return 0;
    const method = shippingMethods.find(m => m.storeShippingMethodId === selectedShippingMethodId);
    return method ? method.cost : 0;
  };
  
  const getItemShippingInfo = (item: any) => {
    if (!selectedShippingMethodId || !item.shippingDetails || item.shippingDetails.length === 0) {
      return { unitCost: 0, totalCost: 0, minDays: null as number | null, maxDays: null as number | null };
    }
    const sd = item.shippingDetails.find((s: any) => s.storeShippingMethodId === selectedShippingMethodId);
    if (!sd) return { unitCost: 0, totalCost: 0, minDays: null, maxDays: null };
    const unitCost = sd.price || 0;
    const totalCost = unitCost * item.quantity;
    return { unitCost, totalCost, minDays: sd.minDays ?? null, maxDays: sd.maxDays ?? null };
  };

  const handleCheckout = async () => {
    try {
      if (!isAuthenticated || !user) {
        Swal.fire({
          icon: 'warning',
          title: t('loginRequired') as string,
          text: t('loginToCheckout') as string,
          confirmButtonText: t('login') as string,
          showCancelButton: true,
          cancelButtonText: t('cancel') as string
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/login');
          }
        });
        return;
      }

      console.log('Checkout - User:', user);
      console.log('Checkout - Token exists:', !!token);
      console.log('Checkout - Token length:', token?.length);
      console.log('Checkout - Is Authenticated:', isAuthenticated);
      
      if (!token || !isAuthenticated) {
        Swal.fire({
          icon: 'warning',
          title: t('sessionExpired') as string,
          text: t('sessionExpiredMsg') as string,
          confirmButtonText: t('login') as string
        }).then(() => {
          navigate('/login');
        });
        return;
      }

      // Ensure userId is a valid number
      const userId = user.id ? Number(user.id) : null;

      if (!userId) {
        Swal.fire({
          icon: 'error',
          title: t('customerIdRequired') as string,
          text: t('customerIdRequiredMsg') as string,
          confirmButtonText: t('ok') as string
        });
        return;
      }

      if (!selectedShippingMethodId && shippingMethods.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: t('shippingMethodRequired') || 'Shipping Method Required',
          text: t('selectShippingMethodMsg') || 'Please select a shipping method',
          confirmButtonText: t('ok') as string
        });
        return;
      }

      if (!selectedPaymentMethodId && paymentMethods.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: t('paymentMethodRequired') || 'Payment Method Required',
          text: t('selectPaymentMethodMsg') || 'Please select a payment method',
          confirmButtonText: t('ok') as string
        });
        return;
      }
      
      const requestBody = {
        storeId: storeId,
        userId: userId,
        totalAmount: toUSD(getTotalPrice()),
        currencyCode: 'USD',
        notes: null,
        paymentMethodId: selectedPaymentMethodId,
        shippingMethodId: selectedShippingMethodId,
        shippingCostUSD: toUSD(getShippingCost()),
        items: items.map(item => {
          const itemPrice = calculateItemPrice(item);
          const applicableTier = getApplicableTier(item);
          return {
            storeItemId: item.id,
            itemId: null,
            storeItemPricingTierId: applicableTier ? applicableTier.storeItemPricingTierId : null,
            itemName: item.name,
            itemImage: item.image,
            quantity: item.quantity,
            unitPrice: toUSD(itemPrice),
            totalPrice: toUSD(itemPrice * item.quantity),
            currencyCode: 'USD'
          };
        })
      };

      const url = `${API_BASE_URL}${ENDPOINTS.CREATE_CART_REQUEST(storeId)}`;
      console.log('Checkout - Request URL:', url);
      console.log('Checkout - Request Body:', requestBody);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('Checkout - Response Status:', response.status);
      console.log('Checkout - Response Headers:', Object.fromEntries(response.headers.entries()));

      if (response.status === 401) {
        // Unauthorized - token expired or invalid
        localStorage.removeItem('authToken');
        Swal.fire({
          icon: 'warning',
          title: t('sessionExpired') as string,
          text: t('sessionExpiredMsg') as string,
          confirmButtonText: t('login') as string
        }).then(() => {
          navigate('/login');
        });
        return;
      }

      if (!response.ok) {
        let errorMessage = t('orderFailed') as string;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.title || errorMessage;
        } catch (e) {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }
        
        Swal.fire({
        icon: 'error',
        title: t('orderFailed') as string,
        text: errorMessage,
        confirmButtonText: t('ok') as string
      });
        return;
      }

      const data = await response.json();
      Swal.fire({
        icon: 'success',
        title: t('orderSuccess') as string,
        html: `${t('orderNumber')}: <strong>${data.cartRequestId || data.id || t('notAvailable')}</strong>`,
        confirmButtonText: t('ok') as string
      });
      clearCart();
      navigate('/');
    } catch (error: any) {
      console.error('Error completing order:', error);
      Swal.fire({
        icon: 'error',
        title: t('error') as string,
        text: error.message || t('orderFailed') as string,
        confirmButtonText: t('ok') as string
      });
    }
  };

  return (
    <div className="cart-page bg-light min-vh-100">
      <Navbar />
      <Container className="py-5">
        <Card className="border-0 shadow-sm rounded-3">
          <Card.Header className="bg-white p-0 border-bottom">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || 'cart')}
              className="profile-tabs border-0"
              fill
            >
              <Tab eventKey="cart" title={
                <div className="py-3">
                  <i className="fas fa-shopping-cart me-2"></i>
                  {t('shoppingCart')}
                  {items.length > 0 && (
                    <Badge bg="success" className="ms-2">{items.length}</Badge>
                  )}
                </div>
              }>
              </Tab>
              {isAuthenticated && (
                <Tab eventKey="orders" title={
                  <div className="py-3">
                    <i className="fas fa-history me-2"></i>
                    {t('previousOrders')}
                  </div>
                }>
                </Tab>
              )}
            </Tabs>
          </Card.Header>
          <Card.Body className="p-0">
            {activeTab === 'cart' && (
              <>
                {items.length === 0 ? (
                  <div className="text-center py-5 empty-cart">
                    <div className="empty-cart-icon mb-4">
                      <i className="fas fa-shopping-cart"></i>
                    </div>
                    <h3 className="mb-3 fw-bold" style={{ color: '#1f2937' }}>{t('emptyCart')}</h3>
                    <p className="text-muted mb-4 fs-5">{t('addItemsToStart')}</p>
                    <Button 
                      variant="success" 
                      onClick={() => navigate('/')}
                      className="empty-cart-btn"
                      size="lg"
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      {t('backToShopping')}
                    </Button>
                  </div>
                ) : (
                  <div className="p-4">
                    <Row>
                      <Col lg={8}>
                        <div className="mb-5">
                          <h5 className="fw-bold mb-3 pb-2 border-bottom d-flex align-items-center">
                            <i className="fas fa-shopping-basket me-2 text-primary"></i>
                            {t('items')}
                          </h5>
                          <div className="table-responsive">
                            <Table className="cart-table mb-0 align-middle" hover responsive>
                              <thead className="bg-light">
                                <tr>
                                  <th className="border-0 py-3 ps-4">{t('image')}</th>
                                  <th className="border-0 py-3">{t('itemName')}</th>
                                  <th className="border-0 py-3 text-center">{t('price')}</th>
                                  <th className="border-0 py-3 text-center">{t('quantity')}</th>
                                  <th className="border-0 py-3 text-center">{t('total')}</th>
                                  <th className="border-0 py-3 text-center">{t('action')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((item) => {
                                  const itemPrice = calculateItemPrice(item);
                                  const applicableTier = getApplicableTier(item);
                                  const itemTotal = itemPrice * item.quantity;
                                  const shipInfo = getItemShippingInfo(item);
                                  const basePrice = item.originalPrice || item.price;
                                  const hasTierDiscount = applicableTier && itemPrice < basePrice;
                                  const displayName = i18n.language === 'en' && item.nameEn ? item.nameEn : item.name;
                                  
                                  return (
                                    <tr key={`${item.id}-${item.quantity}`} className="border-bottom">
                                      <td className="ps-4 py-3">
                                        <div className="cart-item-image-wrapper" style={{width: '80px', height: '80px'}}>
                                          <img
                                            src={item.image}
                                            alt={displayName}
                                            className="cart-item-image w-100 h-100 object-fit-cover rounded"
                                            onClick={() => navigate(`/item/${item.id}`)}
                                            style={{ cursor: 'pointer' }}
                                          />
                                        </div>
                                      </td>
                                      <td className="py-3">
                                        <div className="cart-item-name">
                                          <strong 
                                            onClick={() => navigate(`/item/${item.id}`)}
                                            style={{ cursor: 'pointer' }}
                                            className="text-dark"
                                          >
                                            {displayName}
                                          </strong>
                                          {applicableTier && (
                                            <div className="mt-1">
                                              <Badge bg="success" className="wholesale-badge">
                                                <i className="fas fa-tag me-1"></i>
                                                {t('wholesale')}: {applicableTier.minQuantity.toFixed(0)} 
                                                {applicableTier.maxQuantity ? ` - ${applicableTier.maxQuantity.toFixed(0)}` : ` - ${t('more')}`}
                                              </Badge>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      <td className="text-center py-3">
                                        <div>
                                          {hasTierDiscount && (
                                            <div className="text-success small mb-1 fw-bold">
                                              <i className="fas fa-check-circle me-1"></i>
                                              {t('priceAdjusted')}
                                            </div>
                                          )}
                                          <span className={`cart-item-price ${hasTierDiscount ? 'text-success fw-bold' : ''}`}>
                                            {formatPrice(itemPrice)}
                                          </span>
                                          {hasTierDiscount && (
                                            <div className="text-muted small text-decoration-line-through">
                                              {formatPrice(basePrice)}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      <td className="text-center py-3">
                                        <div className="quantity-controls d-flex align-items-center justify-content-center gap-2">
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="quantity-btn rounded-circle p-0 d-flex align-items-center justify-content-center"
                                            style={{width: '32px', height: '32px'}}
                                            onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                            disabled={item.quantity <= (item.minimumOrderQuantity || 1)}
                                          >
                                            <i className="fas fa-minus"></i>
                                          </Button>
                                          <Badge bg="light" text="dark" className="quantity-badge fs-6 border px-3 py-2">
                                            {item.quantity}
                                          </Badge>
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="quantity-btn rounded-circle p-0 d-flex align-items-center justify-content-center"
                                            style={{width: '32px', height: '32px'}}
                                            onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                          >
                                            <i className="fas fa-plus"></i>
                                          </Button>
                                        </div>
                                      </td>
                                      <td className="text-center py-3">
                                        <span className="cart-item-total fw-bold text-primary">{formatPrice(itemTotal)}</span>
                                        {selectedShippingMethodId && (
                                          <div className="small text-muted mt-1">
                                            <div>
                                              <i className="fas fa-truck me-1"></i>
                                              {t('shippingPerItem')}: {formatPrice(shipInfo.unitCost)} × {item.quantity} = <span className="fw-bold">{formatPrice(shipInfo.totalCost)}</span>
                                            </div>
                                            {shipInfo.minDays && (
                                              <div>
                                                <i className="fas fa-clock me-1"></i>
                                                {t('shippingDays')}: {shipInfo.minDays}{shipInfo.maxDays ? `-${shipInfo.maxDays}` : ''} {t('days')}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      <td className="text-center py-3">
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          className="remove-btn border-0"
                                          onClick={() => removeFromCart(item.id)}
                                          title={t('removeFromCart') as string}
                                        >
                                          <i className="fas fa-trash-alt"></i>
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                          <div className="d-flex justify-content-start mt-3">
                            <Button 
                              variant="outline-danger" 
                              onClick={clearCart}
                              className="clear-cart-btn btn-sm"
                            >
                              <i className="fas fa-trash-alt me-2"></i>
                              {t('clearCart')}
                            </Button>
                          </div>
                        </div>

                        <div className="mb-5">
                          <h5 className="fw-bold mb-3 pb-2 border-bottom d-flex align-items-center">
                            <i className="fas fa-truck me-2 text-primary"></i>
                            {t('shippingMethod') || 'Shipping Method'}
                          </h5>
                          {items.length > 0 && (
                            <div className="mb-3">
                              {(() => {
                                const allExternal = items.every(i => i.isExternalOrder);
                                const noneExternal = items.every(i => !i.isExternalOrder);
                                return (
                                  <Alert variant={allExternal ? 'info' : noneExternal ? 'secondary' : 'warning'} className="py-2">
                                    <i className="fas fa-info-circle me-2"></i>
                                    <strong>{t('orderType')}:</strong>{' '}
                                    {allExternal ? t('externalOrderType') : noneExternal ? t('internalOrderType') : (t('externalShippingOnly') || 'طلب مختلط: الشحن سيحسب للأصناف الخارجية فقط')}
                                  </Alert>
                                );
                              })()}
                            </div>
                          )}
                          {shippingMethods.length === 0 ? (
                            <Alert variant="info">
                              <i className="fas fa-info-circle me-2"></i>
                              {t('noShippingMethods') || 'No shipping methods available'}
                            </Alert>
                          ) : (
                            <Row className="g-3">
                              {(() => {
                                const hasExternal = items.some(i => i.isExternalOrder);
                                const hasInternal = items.some(i => !i.isExternalOrder);
                                if (hasExternal && hasInternal) {
                                  return (
                                    <Col xs={12}>
                                      <Alert variant="warning" className="py-2">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {t('externalShippingOnly') || 'سيتم احتساب وتطبيق الشحن على الأصناف الخارجية فقط. الأصناف المحلية لا تشمل الشحن ضمن هذا الطلب.'}
                                      </Alert>
                                    </Col>
                                  );
                                }
                                return null;
                              })()}
                              {shippingMethods.map((method) => {
                                const cost = method.cost;
                                const isSelected = selectedShippingMethodId === method.storeShippingMethodId;
                                return (
                                  <Col md={6} key={method.storeShippingMethodId}>
                                    <div 
                                      className={`p-3 rounded border cursor-pointer h-100 ${isSelected ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-white'}`}
                                      style={{ transition: 'all 0.2s', cursor: 'pointer' }}
                                      onClick={() => setSelectedShippingMethodId(method.storeShippingMethodId)}
                                    >
                                      <Form.Check
                                        type="radio"
                                        id={`shipping-${method.storeShippingMethodId}`}
                                        name="shippingMethod"
                                        checked={isSelected}
                                        onChange={() => setSelectedShippingMethodId(method.storeShippingMethodId)}
                                        label={
                                          <div className="d-flex justify-content-between align-items-center w-100 ps-2">
                                            <div>
                                              <div className="fw-bold">{i18n.language === 'en' && method.nameEn ? method.nameEn : method.nameAr}</div>
                                              <small className="text-muted">
                                                <i className="fas fa-clock me-1"></i>
                                                {method.estimatedDays ? `${method.estimatedDays} ${t('days')}` : t('calculating')}
                                              </small>
                                            </div>
                                            <div className="fw-bold text-primary">
                                              {cost > 0 ? formatPrice(cost) : <span className="text-success">{t('free')}</span>}
                                            </div>
                                          </div>
                                        }
                                        className="w-100 mb-0"
                                      />
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          )}
                        </div>

                        <div className="mb-4">
                          <h5 className="fw-bold mb-3 pb-2 border-bottom d-flex align-items-center">
                            <i className="fas fa-credit-card me-2 text-primary"></i>
                            {t('paymentMethod') || 'Payment Method'}
                          </h5>
                          {paymentMethods.length === 0 ? (
                            <Alert variant="info">
                              <i className="fas fa-info-circle me-2"></i>
                              {t('noPaymentMethods') || 'No payment methods available'}
                            </Alert>
                          ) : (
                            <Row className="g-3">
                              {(() => {
                                const allExternal = items.length > 0 && items.every(i => i.isExternalOrder);
                                const visiblePaymentMethods = paymentMethods.filter((m) => {
                                  const methodLabel = i18n.language === 'en' && m.nameEn ? m.nameEn : m.nameAr;
                                  const n = (methodLabel || '').toLowerCase();
                                  const isInstallment = n.includes('tabby') || n.includes('tamara') || (methodLabel || '').includes('تابي') || (methodLabel || '').includes('تمارا');
                                  return isInstallment ? allExternal : true;
                                });
                                return visiblePaymentMethods.map((method) => {
                                const isSelected = selectedPaymentMethodId === method.storePaymentMethodId;
                                const methodLabel = i18n.language === 'en' && method.nameEn ? method.nameEn : method.nameAr;
                                const hasExternal = items.some(i => i.isExternalOrder);
                                const isInstallment = hasExternal && methodLabel && (methodLabel.toLowerCase().includes('tabby') || methodLabel.toLowerCase().includes('tamara') || methodLabel.includes('تابي') || methodLabel.includes('تمارا'));
                                const totalAmount = getTotalPrice() + getShippingCost();
                                const installmentAmount = totalAmount / 4;

                                return (
                                  <Col md={6} key={method.storePaymentMethodId}>
                                    <div 
                                      className={`p-3 rounded border cursor-pointer h-100 ${isSelected ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-white'}`}
                                      style={{ transition: 'all 0.2s', cursor: 'pointer' }}
                                      onClick={() => setSelectedPaymentMethodId(method.storePaymentMethodId)}
                                    >
                                      <Form.Check
                                        type="radio"
                                        id={`payment-${method.storePaymentMethodId}`}
                                        name="paymentMethod"
                                        checked={isSelected}
                                        onChange={() => setSelectedPaymentMethodId(method.storePaymentMethodId)}
                                        label={
                                          <div className="w-100 ps-2">
                                            <div className="d-flex align-items-center mb-1">
                                              {method.image ? (
                                                <img src={method.image} alt={methodLabel} style={{ height: '24px', marginRight: '10px' }} />
                                              ) : (
                                                (() => {
                                                  const n = methodLabel?.toLowerCase() || '';
                                                  if (n.includes('mada') || n.includes('مدى')) return <i className="fas fa-credit-card text-primary fa-lg me-2"></i>;
                                                  if (n.includes('tabby') || n.includes('تابي')) return <i className="fas fa-hand-holding-dollar text-success fa-lg me-2"></i>;
                                                  if (n.includes('tamara') || n.includes('تمارا')) return <i className="fas fa-money-bill-wave text-warning fa-lg me-2"></i>;
                                                  if (n.includes('bank') || n.includes('transfer') || n.includes('تحويل') || n.includes('بنكي')) return <i className="fas fa-university text-secondary fa-lg me-2"></i>;
                                                  return <i className="fas fa-wallet text-muted fa-lg me-2"></i>;
                                                })()
                                              )}
                                              <div className="fw-bold">{methodLabel}</div>
                                            </div>
                                            {isInstallment && totalAmount > 0 && (
                                              <div className="mt-2 small text-muted bg-white p-2 rounded border border-light">
                                                <div className="d-flex align-items-center text-success mb-1">
                                                  <i className="fas fa-money-bill-wave me-1"></i>
                                                  <strong>{t('payInInstallments')}</strong>
                                                </div>
                                                <div>
                                                  {t('splitIn4', { amount: formatPrice(installmentAmount) })}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        }
                                        className="w-100 mb-0"
                                      />
                                    </div>
                                  </Col>
                                );
                                });
                              })()}
                            </Row>
                          )}
                        </div>
                      </Col>

                      <Col lg={4}>
                        <div className="bg-light p-4 rounded-3 h-100 sticky-top" style={{ top: '80px', zIndex: 1 }}>
                          <h5 className="fw-bold mb-4">{t('orderSummary')}</h5>
                          
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">{t('subtotal')}</span>
                            <span className="fw-bold">{formatPrice(getTotalPrice())}</span>
                          </div>
                          
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">{t('shippingCost')}</span>
                            <span className="fw-bold">{formatPrice(getShippingCost())}</span>
                          </div>
                          
                          {(() => {
                            const selected = paymentMethods.find(m => m.storePaymentMethodId === selectedPaymentMethodId);
                            const methodLabel = selected ? (i18n.language === 'en' && selected.nameEn ? selected.nameEn : selected.nameAr) : '';
                            const n = (methodLabel || '').toLowerCase();
                            const isInstallment = n.includes('tabby') || n.includes('tamara') || (methodLabel || '').includes('تابي') || (methodLabel || '').includes('تمارا');
                            const baseTotal = getTotalPrice() + getShippingCost();
                            const percentage = selected && typeof selected.feePercentage === 'number' ? selected.feePercentage : (isInstallment ? 2.5 : 0);
                            const feeAmount = (baseTotal * percentage) / 100;
                            return feeAmount > 0 ? (
                              <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">
                                  {t('paymentFee')}
                                  {methodLabel ? ` - ${methodLabel} (${percentage}%)` : ` (${percentage}%)`}
                                </span>
                                <span className="fw-bold">{formatPrice(feeAmount)}</span>
                              </div>
                            ) : null;
                          })()}
                          
                          <hr className="my-4" />
                          
                          {(() => {
                            const selected = paymentMethods.find(m => m.storePaymentMethodId === selectedPaymentMethodId);
                            const methodLabel = selected ? (i18n.language === 'en' && selected.nameEn ? selected.nameEn : selected.nameAr) : '';
                            const n = (methodLabel || '').toLowerCase();
                            const isInstallment = n.includes('tabby') || n.includes('tamara') || (methodLabel || '').includes('تابي') || (methodLabel || '').includes('تمارا');
                            const baseTotal = getTotalPrice() + getShippingCost();
                            const percentage = selected && typeof selected.feePercentage === 'number' ? selected.feePercentage : (isInstallment ? 2.5 : 0);
                            const feeAmount = (baseTotal * percentage) / 100;
                            const finalTotal = baseTotal + feeAmount;
                            return (
                              <div className="d-flex justify-content-between mb-4">
                                <span className="fs-5 fw-bold">{t('total')}</span>
                                <span className="fs-5 fw-bold text-primary">
                                  {formatPrice(finalTotal)}
                                </span>
                              </div>
                            );
                          })()}
                          
                          <Button 
                            variant="primary" 
                            size="lg" 
                            className="w-100 rounded-pill"
                            onClick={handleCheckout}
                            disabled={items.length === 0}
                          >
                            {t('placeOrder')}
                            <i className="fas fa-check-circle ms-2"></i>
                          </Button
                          > 
                          
                          <div className="text-center mt-3">
                            <small className="text-muted">
                              <i className="fas fa-lock me-1"></i>
                              {t('secureCheckout')}
                            </small>
                          </div>

                          <div className="mt-4 pt-3 border-top">
                            <p className="small fw-bold text-muted mb-2">{t('weAccept')}</p>
                            <div className="d-flex gap-2 justify-content-center flex-wrap">
                               <Badge bg="light" text="dark" className="border px-2 py-1"><i className="fas fa-credit-card me-1 text-primary"></i> Mada</Badge>
                               {items.length > 0 && items.every(i => i.isExternalOrder) && (
                                 <>
                                   <Badge bg="light" text="dark" className="border px-2 py-1"><i className="fas fa-money-bill-wave me-1 text-success"></i> Tabby</Badge>
                                   <Badge bg="light" text="dark" className="border px-2 py-1"><i className="fas fa-money-bill-wave me-1 text-warning"></i> Tamara</Badge>
                                 </>
                               )}
                               <Badge bg="light" text="dark" className="border px-2 py-1"><i className="fas fa-university me-1 text-secondary"></i> {t('bankTransfer')}</Badge>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}
              </>
            )}


            {activeTab === 'orders' && isAuthenticated && (
              <div className="p-4">
                {loadingRequests ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="success" />
                    <p className="mt-2 text-muted">{t('loading') || 'Loading...'}</p>
                  </div>
                ) : cartRequests.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="empty-cart-icon mb-3" style={{ width: '80px', height: '80px', fontSize: '2.5rem' }}>
                      <i className="fas fa-box-open"></i>
                    </div>
                    <p className="text-muted fw-medium">{t('noOrdersFound') || 'No previous orders found'}</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="orders-table mb-0 table-hover align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th className="py-3 ps-4">{t('orderNumber') || 'Order #'}</th>
                          <th className="py-3">{t('requestDate') || 'Date'}</th>
                          <th className="py-3 text-center">{t('items') || 'Items'}</th>
                          <th className="py-3">{t('total') || 'Total'}</th>
                          <th className="py-3">{t('status') || 'Status'}</th>
                          <th className="py-3 text-end pe-4">{t('actions') || 'Actions'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cartRequests.map((req) => (
                          <tr key={req.cartRequestId} className="order-row border-bottom">
                            <td className="ps-4 py-3">
                              <span className="order-id-badge badge bg-light text-dark border">#{req.cartRequestId}</span>
                            </td>
                            <td className="py-3">
                              <div className="fw-medium text-dark">
                                {new Date(req.requestDate).toLocaleDateString()}
                              </div>
                              <small className="text-muted">
                                {new Date(req.requestDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </small>
                            </td>
                            <td className="text-center py-3">
                              <Badge bg="secondary" className="rounded-pill">
                                {req.items?.length || 0}
                              </Badge>
                            </td>
                            <td className="py-3">
                              <div className="fw-bold text-success">
                                {formatPrice(req.totalAmount)}
                              </div>
                            </td>
                            <td className="py-3">
                              {getStatusBadge(req.requestStatus, req.requestStatusName)}
                            </td>
                            <td className="text-end pe-4 py-3">
                                <Button variant="link" className="text-decoration-none p-0 text-secondary">
                                    <i className="fas fa-eye"></i>
                                </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Cart;

